import { useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ThemeContext } from "../../context/ThemeContext";
import api from "../../api/axios";
import config from "../../config/config";
import Logo from "../../components/logo/Logo";
import FloatingInput from "../../components/input/FloatingInput";
import { useTranslation } from "react-i18next";
import { useCompanyAuthPath } from "../../hooks/useCompanyAuthPath";

export default function ForgotPassword() {
  const { t: trans } = useTranslation();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useContext(ThemeContext);
  const { companyPath } = useCompanyAuthPath();


  // ✅ Schema with translation
  const schema = useMemo(() => {
    return yup.object().shape({
      email: yup
        .string()
        .email(trans("validation_email_invalid"))
        .required(trans("validation_email_required")),
    });
  }, [trans]);


  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  // ✅ Handle form submission
  const onSubmit = async (data) => {
    setSuccessMessage("");
    setErrorMessage("");
    setIsLoading(true);

    try {
      if (theme.id) data.maincompanyid = theme.id;

      const res = await api.post("/auth/forgot-password", {
        ...data,
        domain: theme.domain_name,
      });

      if (res.data?.success) {
        setSuccessMessage(res.data.message || trans("forgot_password_success"));
      } else {
        setErrorMessage(res.data?.message || trans("forgot_password_error"));
      }

      setTimeout(() => navigate(companyPath("")), 2200);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || trans("forgot_password_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="container-full bg-login"
      style={
        theme?.background_image
          ? {
            background: `url(${config.assetsBaseUrl}/${theme.background_image}) no-repeat center / cover`,
          }
          : {}
      }
    >
      <div className="bg-layer">
        <div className="auth-container">
          <div className="auth-screens">
            <div className="company-logo">
              <Logo type="primary" size={196} />
            </div>

            <div
              className={`auth-heading ${errorMessage
                ? "invalidcredentials"
                : successMessage
                  ? "successcredentials"
                  : ""
                }`}
            >
              <h2>{trans("forgot_password_title")}</h2>
              <p className="heading-b">{trans("forgot_password_instructions")}</p>

              {/* ✅ Success Message */}
              {successMessage && <div className="field-error">{successMessage}</div>}

              {/* ✅ Error Message */}
              {errorMessage && <div className="field-error">{errorMessage}</div>}
            </div>

            <div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="input-field">
                  <div className="form-group">
                    <FloatingInput
                      type="email"
                      name="email"
                      label={trans("forgot_password_label_email")}
                      className="inpt-pla"
                      {...register("email")}
                    />
                    {errors.email && (
                      <p className="field-error">{errors.email.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="submit-btn btn-bg inpt-btn"
                  >
                    {isLoading
                      ? trans("forgot_password_sending")
                      : trans("forgot_password_button")}
                  </button>
                </div>
              </form>
            </div>

            <div className="auth-ftr">
              <p className="heading-b">
                {trans("forgot_password_remembered")}{" "}
                <Link to={companyPath("")} rel="noopener noreferrer" className="heading-b" >{trans("forgot_password_go_back")}</Link>
              </p>
            </div>
          </div>

          <div className="company-logo top-15">
            <Logo type="secondaryWhite" size={217} />
          </div>
        </div>
      </div>
    </div>
  );
}
